import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Grid, Input, Message, Modal, Radio, Table, Tag } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';

import { INTERNAL_MODEL_TYPE } from '@/pages/data-center/model-center/model-provider-management/components/ProviderStrategyModal/constants';
import { getModelStrategies } from '@/pages/data-center/model-center/model-subscription/service';
import type { ModelSubscriptionStrategyItem } from '@/pages/data-center/model-center/model-subscription/types';

/** 已订阅策略的置灰样式。 */
const SUBSCRIBED_ROW_STYLE = { opacity: 0.45 } as const;

/** 模型服务步骤组件属性。 */
interface BindingProviderStepProps {
  /** 当前表单实例。 */
  form: FormInstance;
  /** 通用必填校验规则。 */
  defaultRules?: { required: boolean; message: string }[];
  /** 编辑态回填数据，用于供应商 / 上游模型展示兜底。 */
  initData?: Record<string, any> | null;
  /** 策略选择只读 */
  readOnly?: boolean;
  /** 计价模式：隐藏 UI，仅注册 strategyName / strategyDisplayName / strategyProvider 字段 */
  hidden?: boolean;
}

/** 提取策略资源名。 */
function getStrategyName(record?: ModelSubscriptionStrategyItem) {
  return record?.metadata?.name || '';
}

/** 统一包装已订阅策略的单元格内容。 */
function renderSubscribedCell(record: ModelSubscriptionStrategyItem, content: ReactNode) {
  return <div style={record?.subscribed ? SUBSCRIBED_ROW_STYLE : undefined}>{content}</div>;
}

/** 内置供应商展示文案。 */
function formatProviderDisplay(provider: string | undefined, builtinLabel: string) {
  const value = String(provider || '').trim();
  if (!value) return '';
  if (value === INTERNAL_MODEL_TYPE) return builtinLabel;
  return value;
}

/** 模型服务表单片段：选择单个模型厂商策略（直建 UI 与计费模式一致的双输入框）。 */
export function BindingProviderStep({ form, defaultRules, initData, readOnly, hidden }: BindingProviderStepProps) {
  const { t } = useTranslation();
  const supplierLabel = t('data-center:model.subscription.supplier');
  const modelLabel = t('data-center:model.subscription.model');
  const builtinLabel = t('data-center:model.provider.builtin');

  /** 当前工作空间下可选策略列表。 */
  const [strategies, setStrategies] = useState<ModelSubscriptionStrategyItem[]>([]);
  /** 策略列表请求加载态。 */
  const [loading, setLoading] = useState(false);
  /** 选择弹窗显示状态。 */
  const [visible, setVisible] = useState(false);
  /** 弹窗内当前临时选中的策略名。 */
  const [selectedValue, setSelectedValue] = useState<string | undefined>();

  /** 监听工作空间字段，按租户维度加载策略。 */
  const workspace = Form.useWatch('workspace', form) as string | undefined;
  /** 上一次租户空间，用于切换租户时移除跨租户的供应商选择。 */
  const previousWorkspaceRef = useRef(form.getFieldValue('workspace'));
  /** 当前选中的策略资源名，提交时仍使用 metadata.name。 */
  const strategyName = Form.useWatch('strategyName', form) as string | undefined;
  /** 表单中的上游模型展示名（customModelID）。 */
  const strategyDisplayNameFromForm = Form.useWatch('strategyDisplayName', form) as string | undefined;
  /** 表单中的模型供应商（spec.provider）。 */
  const strategyProviderFromForm = Form.useWatch('strategyProvider', form) as string | undefined;

  /** 编辑态详情中的 customModelID 兜底展示值。 */
  const strategyDisplayName = String(strategyDisplayNameFromForm || initData?.strategyDisplayName || '').trim();
  /** 编辑态详情中的供应商兜底展示值。 */
  const strategyProvider = String(strategyProviderFromForm || initData?.strategyProvider || '').trim();

  const providerDisplay = formatProviderDisplay(strategyProvider, builtinLabel);
  const modelDisplay = strategyDisplayName;
  const hasWorkspace = Boolean(String(workspace || '').trim());
  const providerDisabled = Boolean(readOnly || !hasWorkspace);
  const providerPlaceholder = hasWorkspace
    ? t('common:select.placeholder.name', { name: supplierLabel })
    : t('common:workspace.placeholder');
  const modelPlaceholder = hasWorkspace ? t('common:select.placeholder.name', { name: modelLabel }) : t('common:workspace.placeholder');

  useEffect(() => {
    if (hidden) return;

    const previousWorkspace = String(previousWorkspaceRef.current || '').trim();
    const currentWorkspace = String(workspace || '').trim();
    if (previousWorkspace && previousWorkspace !== currentWorkspace) {
      form.setFieldsValue({
        strategyName: undefined,
        strategyDisplayName: undefined,
        strategyProvider: undefined,
      });
      setStrategies([]);
      setSelectedValue(undefined);
      setVisible(false);
    }
    previousWorkspaceRef.current = workspace;
  }, [form, hidden, workspace]);

  useEffect(() => {
    if (hidden) return;

    const normalizedWorkspace = String(workspace || '').trim();
    if (!normalizedWorkspace) {
      setStrategies([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    /** 拉取策略列表。 */
    const load = async () => {
      try {
        setLoading(true);
        const res = await getModelStrategies({ workspace: normalizedWorkspace });
        if (cancelled) return;
        setStrategies(Array.isArray(res?.items) ? res.items : []);
      } catch (error: any) {
        Message.error(error?.message || t('data-center:model.subscription.provider.fetch.failed'));
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [hidden, t, workspace]);

  useEffect(() => {
    if (hidden) return;

    /** 当前表单中的策略名。 */
    const currentStrategyName = String(form.getFieldValue('strategyName') || '').trim();
    if (currentStrategyName || !strategyDisplayName || !strategies.length) {
      return;
    }

    /** 通过 customModelID 反查真实策略名。 */
    const matchedStrategy = strategies.find((item) => String(item?.spec?.customModelID || '').trim() === strategyDisplayName);
    if (!matchedStrategy?.metadata?.name) {
      return;
    }
    form.setFieldValue('strategyName', matchedStrategy.metadata.name);
    const provider = String(matchedStrategy.spec?.provider || '').trim();
    if (provider && !form.getFieldValue('strategyProvider')) {
      form.setFieldValue('strategyProvider', provider);
    }
  }, [form, hidden, strategies, strategyDisplayName]);

  useEffect(() => {
    if (hidden || !strategyName || !strategies.length) return;

    /** 配置预览：供应商取弹窗「模型供应商」，模型取「上游模型」customModelID */
    const selectedStrategy = strategies.find((item) => getStrategyName(item) === strategyName);
    const customModelID = String(selectedStrategy?.spec?.customModelID || '').trim();
    if (customModelID && form.getFieldValue('strategyDisplayName') !== customModelID) {
      form.setFieldValue('strategyDisplayName', customModelID);
    }
    const provider = String(selectedStrategy?.spec?.provider || '').trim();
    if (form.getFieldValue('strategyProvider') !== provider) {
      form.setFieldValue('strategyProvider', provider || undefined);
    }
  }, [form, hidden, strategies, strategyName]);

  const columns = useMemo(
    () => [
      {
        title: '',
        width: 56,
        render: (_v: unknown, record: ModelSubscriptionStrategyItem) =>
          renderSubscribedCell(record, <Radio disabled={record.subscribed} checked={selectedValue === getStrategyName(record)} />),
      },
      {
        title: t('data-center:model.subscription.provider'),
        dataIndex: 'spec.provider',
        render: (_v: unknown, record: ModelSubscriptionStrategyItem) =>
          renderSubscribedCell(record, record?.spec?.provider === INTERNAL_MODEL_TYPE ? builtinLabel : record?.spec?.provider || '-'),
      },
      {
        title: t('data-center:model.real.name'),
        dataIndex: 'spec.customModelID',
        render: (_v: unknown, record: ModelSubscriptionStrategyItem) => renderSubscribedCell(record, record?.spec?.customModelID || '-'),
      },
      {
        title: t('data-center:model.subscription.subscribed'),
        dataIndex: 'subscribed',
        width: 100,
        render: (subscribed: boolean | undefined, record: ModelSubscriptionStrategyItem) =>
          renderSubscribedCell(
            record,
            <Tag color={subscribed ? 'green' : 'gray'}>
              {subscribed ? t('data-center:model.subscription.subscribed.yes') : t('data-center:model.subscription.subscribed.no')}
            </Tag>,
          ),
      },
      {
        title: t('common:description'),
        dataIndex: 'spec.description',
        ellipsis: true,
        render: (_v: unknown, record: ModelSubscriptionStrategyItem) => renderSubscribedCell(record, record?.spec?.description || '-'),
      },
    ],
    [builtinLabel, selectedValue, t],
  );

  const openModal = () => {
    if (providerDisabled) return;
    setSelectedValue(strategyName);
    setVisible(true);
  };

  /** 确认选择策略：模型供应商 → strategyProvider，上游模型 → strategyDisplayName */
  const handleOk = () => {
    if (!selectedValue) {
      Message.warning(t('data-center:model.subscription.provider.placeholder'));
      return;
    }
    const nextSelectedStrategy = strategies.find((item) => getStrategyName(item) === selectedValue);
    if (nextSelectedStrategy?.subscribed) {
      return;
    }
    form.setFieldsValue({
      strategyName: selectedValue,
      strategyDisplayName: nextSelectedStrategy?.spec?.customModelID || nextSelectedStrategy?.metadata?.name || selectedValue,
      strategyProvider: nextSelectedStrategy?.spec?.provider || '',
    });
    void form.validate(['strategyName']).catch(() => undefined);
    setVisible(false);
  };

  if (hidden) {
    return (
      <>
        <Form.Item field="strategyName" hidden>
          <Input />
        </Form.Item>
        <Form.Item field="strategyDisplayName" hidden>
          <Input />
        </Form.Item>
        <Form.Item field="strategyProvider" hidden>
          <Input />
        </Form.Item>
      </>
    );
  }

  return (
    <>
      <Form.Item field="strategyName" rules={defaultRules} validateTrigger={['onSubmit', 'onChange']} hidden>
        <Input />
      </Form.Item>
      <Form.Item field="strategyDisplayName" hidden>
        <Input />
      </Form.Item>
      <Form.Item field="strategyProvider" hidden>
        <Input />
      </Form.Item>

      <div style={{ fontWeight: 500, marginBottom: 12 }}>{t('data-center:model.subscription.provider')}</div>
      <Grid.Row gutter={16}>
        <Grid.Col span={12}>
          <Form.Item label={supplierLabel} required={!readOnly}>
            <Input readOnly disabled={providerDisabled} value={providerDisplay} placeholder={providerPlaceholder} onClick={openModal} />
          </Form.Item>
        </Grid.Col>
        <Grid.Col span={12}>
          <Form.Item label={modelLabel} required={!readOnly}>
            <Input readOnly disabled={providerDisabled} value={modelDisplay} placeholder={modelPlaceholder} onClick={openModal} />
          </Form.Item>
        </Grid.Col>
      </Grid.Row>

      <Modal
        style={{ width: 900 }}
        visible={visible}
        maskClosable={false}
        title={t('data-center:model.subscription.provider.select.title')}
        onCancel={() => setVisible(false)}
        onOk={handleOk}
        unmountOnExit
      >
        <Table
          rowKey={(record: ModelSubscriptionStrategyItem) => getStrategyName(record)}
          loading={loading}
          pagination={false}
          data={strategies}
          scroll={{ y: '50vh' }}
          columns={columns}
          onRow={(record) => ({
            onClick: () => {
              if (record.subscribed) return;
              setSelectedValue(getStrategyName(record));
            },
          })}
        />
      </Modal>
    </>
  );
}
